import type { Filter } from '@medplum/core'
import type { OrpcClient } from '@/hooks/useClient'

// Derived from the contract so the query shape stays in sync with POST /v2/patients.
type ListPatientsInput = Parameters<OrpcClient['listPatients']>[0]
export type PatientRow = Awaited<ReturnType<OrpcClient['listPatients']>>['data'][number]
type FilterExpr = NonNullable<ListPatientsInput['filter']>
type OrderEntry = NonNullable<ListPatientsInput['order']>[number]
export type OrderDir = 'asc' | 'desc'

// Fields the badges/tooltip and photo cell need beyond the base response.
export const PATIENT_LIST_INCLUDE = [
  'photo',
  'lastLogin',
  'lastInvitationSent',
  'unmatched',
] as const

// ---- Sorting ---------------------------------------------------------------------------------------

// UI column code → model order field. Absent codes (photo, portal-status) aren't server-sortable:
// `name` sorts by family (the model can't sort the composite name), portal status is computed.
const ORDER_FIELD: Record<string, OrderField> = {
  name: 'family',
  gender: 'gender',
  birthdate: 'birthday',
  phone: 'phone',
  email: 'email',
  'address-line': 'address',
  'address-city': 'city',
  'address-state': 'state',
  'address-postalcode': 'zip',
}

type OrderField =
  | 'family'
  | 'gender'
  | 'birthday'
  | 'phone'
  | 'email'
  | 'address'
  | 'city'
  | 'state'
  | 'zip'

export function isSortable(code: string): boolean {
  return code in ORDER_FIELD
}

export function orderFor(code: string, dir: OrderDir): OrderEntry[] | undefined {
  const field = ORDER_FIELD[code]
  return field ? [{ [field]: dir } as OrderEntry] : undefined
}

// ---- Filtering -------------------------------------------------------------------------------------

const GENDER_LABELS: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
  unknown: 'Unknown',
}

// UI column code → model filter field + kind. `gender` is a categorical enum; `birthdate` a date;
// the rest are free-text string columns. Codes absent here (photo, portal-status) aren't filterable.
const FILTER_FIELD: Record<string, { field: string; kind: 'string' | 'date' | 'gender' }> = {
  name: { field: 'name', kind: 'string' },
  gender: { field: 'gender', kind: 'gender' },
  birthdate: { field: 'birthday', kind: 'date' },
  phone: { field: 'phone', kind: 'string' },
  email: { field: 'email', kind: 'string' },
  'address-line': { field: 'address', kind: 'string' },
  'address-city': { field: 'city', kind: 'string' },
  'address-state': { field: 'state', kind: 'string' },
  'address-postalcode': { field: 'zip', kind: 'string' },
}

// Fields the free-text search box unions with `contains`.
const SEARCH_FIELDS = ['name', 'phone', 'email'] as const

const leaf = (obj: Record<string, unknown>): FilterExpr =>
  ({ type: 'filter', ...obj }) as FilterExpr

// Normalize an operand to the model's `YYYY-MM-DD`; null when unparseable (the model ignores such
// filters). ISO date-only strings pass through; other formats are parsed and read back in LOCAL time
// so the calendar day the user entered is preserved.
function toIsoDate(operand: string): string | null {
  const trimmed = operand.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return Number.isNaN(Date.parse(trimmed)) ? null : trimmed
  }
  const t = Date.parse(trimmed)
  if (Number.isNaN(t)) return null
  const d = new Date(t)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

// Medplum filter operators the endpoint can express. `before`/`after` are inclusive (≤/≥); the strict
// gt/lt map onto them (boundary treated as a match). Unsupported operators (sw, above, in, …) → null.
function stringCond(op: string, value: string): Record<string, string> | null {
  switch (op) {
    case 'eq':
      return { exact: value }
    case 'ne':
    case 'not':
      return { not_exact: value }
    case 'gt':
    case 'ge':
    case 'sa':
      return { after: value }
    case 'lt':
    case 'le':
    case 'eb':
      return { before: value }
    case 'contains':
    case 'sw':
      return { contains: value }
    default:
      return null
  }
}

function dateCond(op: string, value: string): Record<string, string> | null {
  const iso = toIsoDate(value)
  if (!iso) return null
  switch (op) {
    case 'eq':
      return { exact: iso }
    case 'ne':
    case 'not':
      return { not_exact: iso }
    case 'gt':
    case 'ge':
    case 'sa':
      return { after: iso }
    case 'lt':
    case 'le':
    case 'eb':
      return { before: iso }
    default:
      return null
  }
}

// Match the operand against gender keys/labels and push an `in` set; ne/not/not-in negate it. Nothing
// matched → null (contributes no clause). eq/ne match a whole label (case-insensitive) — a substring
// match would let "male" also select "female" (fe-MALE); only `contains`/`sw` match substrings.
function genderExpr(op: string, value: string): FilterExpr | null {
  const v = value.trim().toLowerCase()
  const keys = Object.keys(GENDER_LABELS)
  const matched =
    op === 'contains' || op === 'sw'
      ? keys.filter((k) => k.includes(v) || GENDER_LABELS[k].toLowerCase().includes(v))
      : keys.filter((k) => k === v || GENDER_LABELS[k].toLowerCase() === v)
  if (!matched.length) return null
  const inLeaf = leaf({ gender: { in: matched } })
  if (op === 'ne' || op === 'not' || op === 'not-in') {
    return { type: 'expression', operator: 'not', operands: [inLeaf] } as FilterExpr
  }
  return inLeaf
}

function searchExpr(text: string): FilterExpr | null {
  const q = text.trim()
  if (!q) return null
  const operands = SEARCH_FIELDS.map((f) => leaf({ [f]: { contains: q } }))
  return { type: 'expression', operator: 'or', operands } as FilterExpr
}

/**
 * Translate the page's free-text search and the FilterMenu's medplum `Filter[]` into the /v2/patients
 * filter tree. Filter rows referencing non-filterable columns or unsupported operators are dropped.
 */
export function buildPatientFilter(searchText: string, filters: Filter[]): FilterExpr | undefined {
  const clauses: FilterExpr[] = []
  for (const f of filters) {
    if (!f.code || !f.operator || f.value === undefined || f.value === '') continue
    const meta = FILTER_FIELD[f.code]
    if (!meta) continue
    const op = String(f.operator)
    const value = String(f.value)
    let expr: FilterExpr | null = null
    if (meta.kind === 'gender') {
      expr = genderExpr(op, value)
    } else if (meta.kind === 'date') {
      const cond = dateCond(op, value)
      if (cond) expr = leaf({ [meta.field]: cond })
    } else {
      const cond = stringCond(op, value)
      if (cond) expr = leaf({ [meta.field]: cond })
    }
    if (expr) clauses.push(expr)
  }
  const search = searchExpr(searchText)
  if (search) clauses.push(search)
  if (clauses.length === 0) return undefined
  if (clauses.length === 1) return clauses[0]
  return { type: 'expression', operator: 'and', operands: clauses } as FilterExpr
}
